/**
 * useOriginNumber — número de ORIGEM por onde a mensagem do contato sai.
 *
 * Funcionalidade B: desacopla "quem envia" (operador/autoria) do "número de
 * ORIGEM" (a conexão física). Carrega GET /origin-options (autorização 100%
 * server-side) e expõe o número padrão (thread ativa), o ativo (override por
 * contato) e as opções que o operador PODE escolher. setOrigin persiste o
 * override por contato (vale para a equipe). Recarrega ao trocar de contato.
 *
 * Quando o recurso está off ou o usuário é solo, options vem vazio e
 * activeOrigin null → a UI esconde o chip/label (comportamento atual).
 */
import { useCallback, useEffect, useState } from 'react';
import assignmentService from '../services/global/assignmentService';

const EMPTY = {
  default_origin: null,
  active_origin: null,
  options: [],
  allow_own: false,
  allow_admin: false,
};

export default function useOriginNumber(phone) {
  const [state, setState] = useState(EMPTY);

  const reload = useCallback(() => {
    if (!phone) {
      setState(EMPTY);
      return undefined;
    }
    let cancelled = false;
    assignmentService
      .getOriginOptions(phone)
      .then((res) => { if (!cancelled) setState(res?.data || EMPTY); })
      .catch((err) => {
        if (!cancelled) setState(EMPTY);
        console.warn('[useOriginNumber] load failed:', err);
      });
    return () => { cancelled = true; };
  }, [phone]);

  useEffect(() => reload(), [reload]);

  // Define o número de origem para ESTE contato (otimista + persiste). origin
  // = uma das options (ou null para voltar ao padrão da thread ativa). A opção
  // carrega o id do dono em `member_id` — o backend espera `origin_user_id`.
  const setOrigin = useCallback(async (origin) => {
    setState((prev) => ({ ...prev, active_origin: origin || prev.default_origin }));
    try {
      const payload = origin && origin.member_id
        ? { origin_user_id: origin.member_id, channel: origin.channel }
        : null;
      await assignmentService.setOrigin(phone, payload);
    } catch (err) {
      console.warn('[useOriginNumber] setOrigin failed:', err);
      reload(); // reverte para o estado do servidor
    }
  }, [phone, reload]);

  const active = state.active_origin || state.default_origin;
  return {
    options: state.options || [],
    defaultOrigin: state.default_origin,
    activeOrigin: active,
    allowOwn: !!state.allow_own,
    allowAdmin: !!state.allow_admin,
    // canSwitch: há mais de uma opção real para escolher.
    canSwitch: (state.options || []).length > 1,
    setOrigin,
    reload,
  };
}
